/**
 * Business Insights — métricas "sticky" para el dueño/admin.
 *
 * Tabs:
 *   - Top clientes del mes (con variación vs mes anterior)
 *   - Productos sin movimiento (30/60/90 días)
 *   - Ranking de vendedores (mes actual)
 *   - Pronóstico próximos 7 días (basado en últimos 28)
 */
import { useEffect, useState } from "react";
import { api } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp, TrendingDown, Minus, Crown, AlertTriangle,
  UserCheck, CalendarDays, Lightbulb, Sparkles, FileDown, FileSpreadsheet, FileText, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KpiCard = ({ label, value, subtitle, Icon, tone = "blue", testid }) => {
  const toneClass = {
    blue: "bg-blue-50 text-[rgb(0,47,167)]",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    violet: "bg-violet-50 text-violet-700",
  }[tone] || "bg-slate-50 text-slate-700";
  return (
    <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">{label}</p>
          <p className="text-xl font-black tracking-tight truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            {value}
          </p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-sm ${toneClass} flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
};

const VariationBadge = ({ pct }) => {
  if (pct === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 text-[11px] font-medium">
        <Minus className="h-3 w-3" /> 0%
      </span>
    );
  }
  const positive = pct > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive
    ? "bg-emerald-50 text-emerald-700"
    : "bg-rose-50 text-rose-700";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {positive ? "+" : ""}{Math.abs(pct).toFixed(1)}%
    </span>
  );
};

function TopCustomersPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/insights/top-customers?limit=10")
      .then(r => setData(r.data))
      .catch(() => toast.error("Error al cargar Top Clientes"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 text-center text-slate-500 text-sm">Cargando…</div>;
  if (!data) return null;

  const rows = data.rows || [];
  const s = data.summary || {};

  return (
    <div data-testid="insights-top-customers">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <KpiCard label="Facturado este mes" value={formatMoney(s.total_billed_month || 0)}
                 subtitle={data.current_month_label} Icon={Crown} tone="blue" testid="kpi-billed-month" />
        <KpiCard label="Mes anterior" value={formatMoney(s.total_billed_prev || 0)}
                 subtitle={data.prev_month_label} Icon={CalendarDays} tone="amber" testid="kpi-billed-prev" />
        <KpiCard label="Clientes activos" value={s.customers_active || 0}
                 subtitle="con compras este mes" Icon={UserCheck} tone="emerald" testid="kpi-customers-active" />
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid="top-customers-card">
        <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Top 10 clientes — {data.current_month_label}
        </h3>
        {rows.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">
            Sin facturas emitidas este mes todavía.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">#</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Cliente</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Facturas</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Mes actual</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Mes anterior</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Variación</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.cliente_id}-${i}`} className="border-b border-slate-100" data-testid={`top-customer-row-${i}`}>
                    <td className="py-2 px-3 text-slate-500 mono">{i + 1}</td>
                    <td className="py-2 px-3 font-medium">{r.nombre_cliente}</td>
                    <td className="py-2 px-3 text-right">{r.facturas_count}</td>
                    <td className="py-2 px-3 text-right font-bold">{formatMoney(r.total_mes)}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{formatMoney(r.total_mes_anterior)}</td>
                    <td className="py-2 px-3 text-right"><VariationBadge pct={r.variation_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function DormantProductsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("90");
  // Supplier filter: "__all__" = no filter; "__none__" = products without proveedor
  // ("" sent to backend); otherwise the literal supplier name.
  const [proveedorFilter, setProveedorFilter] = useState("__all__");
  const [suppliers, setSuppliers] = useState([]);
  const [hasUnassigned, setHasUnassigned] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Map UI filter value → query param (null when no filter).
  const buildProveedorParam = () => {
    if (proveedorFilter === "__all__") return null;
    if (proveedorFilter === "__none__") return "";
    return proveedorFilter;
  };

  const loadData = (provQ) => {
    const params = provQ === null ? {} : { proveedor: provQ };
    setLoading(true);
    api.get("/insights/dormant-products", { params })
      .then(r => setData(r.data))
      .catch(() => toast.error("Error al cargar productos sin movimiento"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Initial: load both suppliers and dormant data.
    api.get("/insights/dormant-products/suppliers")
      .then(r => {
        setSuppliers(r.data?.suppliers || []);
        setHasUnassigned(!!r.data?.has_unassigned);
      })
      .catch(() => {/* non-blocking */});
  }, []);

  useEffect(() => {
    loadData(buildProveedorParam());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorFilter]);

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ bucket, format });
      const provQ = buildProveedorParam();
      if (provQ !== null) params.append("proveedor", provQ);
      const res = await fetch(`${API}/insights/dormant-products/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const ext = format === "excel" ? "xlsx" : "pdf";
      const provSuffix = provQ ? `-${provQ.replace(/\s+/g, "_").slice(0, 40)}` : "";
      const fname = `productos-sin-movimiento-${bucket}d${provSuffix}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format === "excel" ? "Excel" : "PDF"} generado`);
    } catch {
      toast.error("Error al generar el reporte");
    } finally {
      setExporting(false);
    }
  };

  const s = (data && data.summary) || {};
  const rows = data ? (data[`bucket_${bucket}`] || []) : [];

  return (
    <div data-testid="insights-dormant-products">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Productos activos" value={s.total_products || 0} Icon={Sparkles} tone="emerald" testid="kpi-total-products" />
        <KpiCard label="Inactivos 30+ días" value={s.dormant_30 || 0} Icon={AlertTriangle} tone="amber" testid="kpi-dormant-30" />
        <KpiCard label="Inactivos 60+ días" value={s.dormant_60 || 0} Icon={AlertTriangle} tone="amber" testid="kpi-dormant-60" />
        <KpiCard label="Inactivos 90+ días" value={s.dormant_90 || 0} Icon={AlertTriangle} tone="rose" testid="kpi-dormant-90" />
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid="dormant-products-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Productos sin venta
          </h3>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Bucket switcher */}
            <div className="flex gap-1">
              {[{ v: "30", label: "30+" }, { v: "60", label: "60+" }, { v: "90", label: "90+" }].map(b => (
                <button
                  key={b.v}
                  onClick={() => setBucket(b.v)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                    bucket === b.v
                      ? "border-[rgb(0,47,167)] bg-[rgb(0,47,167)] text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`dormant-bucket-${b.v}`}
                >
                  {b.label} días
                </button>
              ))}
            </div>

            {/* Supplier filter */}
            <Select value={proveedorFilter} onValueChange={setProveedorFilter}>
              <SelectTrigger className="rounded-sm h-8 w-[220px] text-xs" data-testid="dormant-supplier-filter">
                <Filter className="h-3 w-3 mr-1 text-slate-400" />
                <SelectValue placeholder="Proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" data-testid="dormant-supplier-all">Todos los proveedores</SelectItem>
                {hasUnassigned && (
                  <SelectItem value="__none__" data-testid="dormant-supplier-none">Sin proveedor asignado</SelectItem>
                )}
                {suppliers.map(name => (
                  <SelectItem key={name} value={name} data-testid={`dormant-supplier-${name}`}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Export buttons */}
            <Button
              onClick={() => handleExport("pdf")}
              disabled={exporting}
              variant="outline" size="sm"
              className="rounded-sm h-8 text-xs"
              data-testid="dormant-export-pdf"
            >
              <FileText className="h-3.5 w-3.5 mr-1" /> PDF
            </Button>
            <Button
              onClick={() => handleExport("excel")}
              disabled={exporting}
              variant="outline" size="sm"
              className="rounded-sm h-8 text-xs"
              data-testid="dormant-export-excel"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
          </div>
        </div>

        {/* Active-filter chip */}
        {proveedorFilter !== "__all__" && (
          <div className="mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-sm bg-blue-50 text-[rgb(0,47,167)] border border-blue-200" data-testid="dormant-filter-chip">
              Filtrado por: {proveedorFilter === "__none__" ? "Sin proveedor asignado" : proveedorFilter}
              <button
                onClick={() => setProveedorFilter("__all__")}
                className="ml-1 hover:text-blue-900"
                data-testid="dormant-filter-clear"
                aria-label="Quitar filtro"
              >×</button>
            </span>
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm" data-testid="dormant-empty">
            No hay productos en este rango con los filtros aplicados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Código</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Descripción</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Proveedor</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Precio</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Días sin venta</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.codigo} className="border-b border-slate-100" data-testid={`dormant-row-${i}`}>
                    <td className="py-2 px-3 mono font-medium">{r.codigo}</td>
                    <td className="py-2 px-3">{r.descripcion}</td>
                    <td className="py-2 px-3 text-slate-600">{r.proveedor || "-"}</td>
                    <td className="py-2 px-3 text-right">{formatMoney(r.precio_venta)}</td>
                    <td className="py-2 px-3 text-right font-bold text-rose-700">
                      {r.dias_sin_venta >= 9999 ? "Nunca" : `${r.dias_sin_venta} días`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SellerRankingPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/insights/seller-ranking")
      .then(r => setData(r.data))
      .catch(() => toast.error("Error al cargar ranking de vendedores"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 text-center text-slate-500 text-sm">Cargando…</div>;
  if (!data) return null;

  const rows = data.rows || [];
  const s = data.summary || {};
  const chartData = rows.slice(0, 8).map(r => ({ name: r.nombre_vendedor || "—", total: r.total_mes }));
  const colors = ["#002FA7", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16"];

  return (
    <div data-testid="insights-seller-ranking">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <KpiCard label="Total vendedores activos" value={s.total_sellers || 0}
                 subtitle={data.current_month_label} Icon={UserCheck} tone="violet" testid="kpi-total-sellers" />
        <KpiCard label="Facturado del mes" value={formatMoney(s.total_billed || 0)}
                 subtitle="por todos los vendedores" Icon={Crown} tone="blue" testid="kpi-sellers-billed" />
        <KpiCard label="Líder del mes"
                 value={rows[0]?.nombre_vendedor || "—"}
                 subtitle={rows[0] ? formatMoney(rows[0].total_mes) : ""}
                 Icon={Sparkles} tone="emerald" testid="kpi-top-seller" />
      </div>

      {chartData.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="seller-chart-card">
          <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Ventas por vendedor — {data.current_month_label}
          </h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid="seller-ranking-card">
        <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Ranking detallado
        </h3>
        {rows.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">
            Sin ventas asignadas a vendedores este mes.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-700">#</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-700">Vendedor</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Facturas</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Clientes</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Ticket promedio</th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700">Total facturado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.nombre_vendedor}-${i}`} className="border-b border-slate-100" data-testid={`seller-row-${i}`}>
                    <td className="py-2 px-3 mono">
                      {i === 0 && <Crown className="h-3.5 w-3.5 inline -mt-0.5 text-amber-500" />} {i + 1}
                    </td>
                    <td className="py-2 px-3 font-medium">{r.nombre_vendedor || "—"}</td>
                    <td className="py-2 px-3 text-right">{r.facturas_count}</td>
                    <td className="py-2 px-3 text-right">{r.clientes_atendidos}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{formatMoney(r.ticket_promedio)}</td>
                    <td className="py-2 px-3 text-right font-bold">{formatMoney(r.total_mes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ForecastPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dias, setDias] = useState(28);
  const [horizonte, setHorizonte] = useState(7);

  useEffect(() => {
    setLoading(true);
    api.get("/insights/sales-forecast", { params: { dias, horizonte } })
      .then(r => setData(r.data))
      .catch(() => toast.error("Error al cargar el pronóstico"))
      .finally(() => setLoading(false));
  }, [dias, horizonte]);

  const rows = data ? (data.forecast_rows || []) : [];
  const s = (data && data.summary) || {};
  const chartData = rows.map(r => ({
    name: `${r.fecha.slice(5)}`,           // MM-DD compact label
    weekday: r.weekday.slice(0, 3),
    expected: r.expected_sales,
    fecha: r.fecha,
  }));

  // Lookback presets — días de historia para basar el promedio.
  const LOOKBACK_PRESETS = [
    { v: 7,  label: "Semana" },
    { v: 28, label: "4 semanas" },
    { v: 30, label: "30 días" },
    { v: 60, label: "60 días" },
    { v: 90, label: "90 días" },
  ];
  // Horizon presets — qué tan lejos proyectamos hacia adelante.
  const HORIZON_PRESETS = [
    { v: 7,  label: "Semana" },
    { v: 30, label: "Mes" },
    { v: 60, label: "60 días" },
    { v: 90, label: "90 días" },
  ];

  const horizonteLabel =
    (HORIZON_PRESETS.find(h => h.v === horizonte) || {}).label || `${horizonte} días`;

  return (
    <div data-testid="insights-forecast">
      {/* Lookback (base) selector */}
      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-3" data-testid="forecast-lookback-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <CalendarDays className="h-4 w-4 text-[rgb(0,47,167)]" />
            <span className="text-slate-700 font-medium">Base de cálculo:</span>
            <span className="text-slate-500">¿Cuántos días querés usar para proyectar?</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {LOOKBACK_PRESETS.map(p => (
              <button
                key={p.v}
                onClick={() => setDias(p.v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                  dias === p.v
                    ? "border-[rgb(0,47,167)] bg-[rgb(0,47,167)] text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`forecast-lookback-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Horizon selector — how far into the future to project. */}
      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-4" data-testid="forecast-horizon-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <span className="text-slate-700 font-medium">Horizonte:</span>
            <span className="text-slate-500">¿Hasta dónde querés proyectar?</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {HORIZON_PRESETS.map(p => (
              <button
                key={p.v}
                onClick={() => setHorizonte(p.v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                  horizonte === p.v
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`forecast-horizon-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="py-10 text-center text-slate-500 text-sm">Cargando…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <KpiCard label={`Próximos ${horizonte} días (estimado)`} value={formatMoney(s.total_next_period || 0)}
                     subtitle={`basado en últimos ${s.sample_days || dias} días`} Icon={TrendingUp} tone="emerald" testid="kpi-forecast-period" />
            <KpiCard label={`Últimos ${s.sample_days || dias} días`} value={formatMoney(s.total_last_period || 0)}
                     subtitle="ventas reales" Icon={CalendarDays} tone="blue" testid="kpi-last-period" />
            <KpiCard label="Promedio diario" value={formatMoney(s.daily_avg || 0)}
                     subtitle={`${s.sample_days || dias} días de muestra`} Icon={Lightbulb} tone="amber" testid="kpi-daily-avg" />
          </div>

          <Card className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="forecast-chart-card">
            <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Pronóstico — próximos {horizonte} días ({horizonteLabel.toLowerCase()})
            </h3>
            {(s.total_last_period || 0) === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm" data-testid="forecast-no-data">
                Aún no hay suficientes datos en los últimos {s.sample_days || dias} días.
                Emití más facturas o probá una ventana más amplia.
              </div>
            ) : (
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey={horizonte <= 14 ? "weekday" : "name"} tick={{ fontSize: 10 }}
                           interval={horizonte > 30 ? Math.ceil(horizonte / 15) : 0} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatMoney(v)}
                      labelFormatter={(_, p) => p?.[0]?.payload?.fecha || ""} />
                    <Bar dataKey="expected" fill="#002FA7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="bg-white border border-slate-200 rounded-sm p-4" data-testid="forecast-table-card">
            <h3 className="text-base font-semibold mb-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Detalle día por día
            </h3>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Fecha</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-700">Día</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-700">Facturas esperadas</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-700">Ventas esperadas</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.fecha} className="border-b border-slate-100" data-testid={`forecast-row-${i}`}>
                      <td className="py-2 px-3 mono">{r.fecha}</td>
                      <td className="py-2 px-3">{r.weekday}</td>
                      <td className="py-2 px-3 text-right">{r.expected_facturas}</td>
                      <td className="py-2 px-3 text-right font-bold">{formatMoney(r.expected_sales)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              * Pronóstico simple basado en el promedio por día de la semana de los últimos {s.sample_days || dias} días,
              proyectado a {horizonte} días hacia adelante. A medida que registres más facturas, la precisión mejora.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

const TABS = [
  { key: "top-customers", label: "Top Clientes", Component: TopCustomersPanel },
  { key: "dormant",       label: "Productos sin movimiento", Component: DormantProductsPanel },
  { key: "sellers",       label: "Ranking Vendedores", Component: SellerRankingPanel },
  { key: "forecast",      label: "Pronóstico", Component: ForecastPanel },
];

export default function Insights() {
  const [tab, setTab] = useState("top-customers");
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    // Tiny delay to let nested panel handle its own loading state.
    const t = setTimeout(() => setBootstrapping(false), 50);
    return () => clearTimeout(t);
  }, []);

  if (bootstrapping) return <PageLoader label="Cargando Insights…" testid="insights-loader" />;

  const ActiveComponent = TABS.find(t => t.key === tab)?.Component || TopCustomersPanel;

  return (
    <div data-testid="insights-page">
      <div className="mb-4">
        <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Insights del negocio
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Métricas inteligentes para tomar mejores decisiones: top clientes, productos
          inactivos, ranking de vendedores y pronóstico de ventas.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[rgb(0,47,167)] text-[rgb(0,47,167)]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`insights-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <ActiveComponent />
    </div>
  );
}
